import { useAtom, useSetAtom } from 'jotai';
import Mousetrap from 'mousetrap';
import QueryString from 'qs';
import React, { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { Route, useLocation } from 'react-router-dom';
import {
  REACT_APP_AUTH_ROLE_ANALYST,
  REACT_APP_AUTH_ROLE_COLLECTOR,
  REACT_APP_AUTH_ROLE_OPERATOR,
} from './AuthProvider';
import { alphaToggleAtom } from './atoms/alpha';
import { ALL_APP_THEMES, fullThemeNameAtom } from './atoms/theme';
import MyCutupsBasic from './components/MyCutupsBasic';
import SecuredRoute from './components/SecuredRoute';
import VideoProvider from './components/VideoProvider';
import { AlignmentIssues } from './pages/AlignmentIssues/AlignmentIssues';
import AlignmentVerificationCompetitionSeason from './pages/AlignmentVerification';
import { AlignmentVerificationStream } from './pages/AlignmentVerification/AlignmentVerificationStream';
import { Approve } from './pages/Approve/Approve';
import Audit from './pages/Audit';
import AwsStreams from './pages/AwsStreams';
import { DownloadFullMatch } from './pages/Download/DownloadFullMatch';
import Edits from './pages/Edits';
import Embed from './pages/Embed';
import FrameExtraction from './pages/FrameExtraction';
import Home from './pages/Home';
import MyPlaylistsBasic from './pages/MyPlaylistsBasic';
import { OffsetRequired } from './pages/OffsetRequired/OffsetRequired';
import { PageWithNavigation } from './pages/PageWithNavigation/PageWithNavigation';
import PlayerBasic from './pages/PlayerBasic';
import Toggles from './pages/Toggles';
import Token from './pages/Token';
import Trim from './pages/Trim';
import Uploads from './pages/Uploads';
import Theme from './providers/Theme';

function App() {
  const location = useLocation();

  const [requiredThemeId, setRequiredThemeId] = useAtom(fullThemeNameAtom);
  const toggleAlphaEnabled = useSetAtom(alphaToggleAtom);

  const { search } = location;
  const query = QueryString.parse(search, { ignoreQueryPrefix: true });
  const { themeId } = query;

  const randomThemeId = () => {
    const random = Math.floor(Math.random() * ALL_APP_THEMES.length);
    setRequiredThemeId(ALL_APP_THEMES[random]);
  };

  useEffect(() => {
    if (themeId) {
      if (themeId === 'madness') {
        setInterval(randomThemeId, 2000);
      } else {
        setRequiredThemeId(themeId);
      }
    }
  }, [themeId, requiredThemeId]);

  Mousetrap.bind('u p t h e r o v e r s', () => {
    toggleAlphaEnabled();
  });
  Mousetrap.bind('u t g', () => {
    toggleAlphaEnabled();
  });

  const ALL_ROLES = [
    REACT_APP_AUTH_ROLE_ANALYST,
    REACT_APP_AUTH_ROLE_COLLECTOR,
    REACT_APP_AUTH_ROLE_OPERATOR,
  ];

  return (
    <Theme themeId={requiredThemeId}>
      <PageWithNavigation>
        <SecuredRoute exact path="/">
          <Home />
        </SecuredRoute>
        <Route exact path="/healthz">
          <FormattedMessage
            defaultMessage="I am alive"
            description="Alive Message"
            id="1be8d"
          />
        </Route>
        <SecuredRoute
          allowed={REACT_APP_AUTH_ROLE_OPERATOR}
          exact
          path="/frame-extraction"
        >
          <FrameExtraction />
        </SecuredRoute>
        <SecuredRoute
          allowed={REACT_APP_AUTH_ROLE_OPERATOR}
          exact
          path="/aws-streams"
        >
          <VideoProvider>
            <AwsStreams />
          </VideoProvider>
        </SecuredRoute>
        <SecuredRoute
          allowed={REACT_APP_AUTH_ROLE_OPERATOR}
          exact
          path="/audit"
        >
          <Audit />
        </SecuredRoute>
        <SecuredRoute
          allowed={REACT_APP_AUTH_ROLE_OPERATOR}
          exact
          path="/upload"
        >
          <Uploads />
        </SecuredRoute>
        <SecuredRoute
          allowed={REACT_APP_AUTH_ROLE_OPERATOR}
          exact
          path="/approve"
        >
          <VideoProvider>
            <Approve />
          </VideoProvider>
        </SecuredRoute>
        <SecuredRoute
          allowed={[REACT_APP_AUTH_ROLE_ANALYST, REACT_APP_AUTH_ROLE_OPERATOR]}
          exact
          path="/my-playlists-basic"
        >
          <VideoProvider>
            <MyPlaylistsBasic />
          </VideoProvider>
        </SecuredRoute>
        <SecuredRoute
          allowed={[REACT_APP_AUTH_ROLE_ANALYST, REACT_APP_AUTH_ROLE_OPERATOR]}
          exact
          path="/my-cutups"
        >
          <VideoProvider>
            <MyCutupsBasic />
          </VideoProvider>
        </SecuredRoute>
        <SecuredRoute allowed={ALL_ROLES} exact path="/player-basic">
          <VideoProvider>
            <PlayerBasic />
          </VideoProvider>
        </SecuredRoute>
        <SecuredRoute
          allowed={ALL_ROLES}
          exact
          path="/download/:sport/:matchId/"
        >
          <VideoProvider>
            <DownloadFullMatch />
          </VideoProvider>
        </SecuredRoute>
        <SecuredRoute allowed={ALL_ROLES} exact path="/download">
          <VideoProvider>
            <DownloadFullMatch />
          </VideoProvider>
        </SecuredRoute>
        <SecuredRoute
          allowed={REACT_APP_AUTH_ROLE_OPERATOR}
          exact
          path="/edits"
        >
          <Edits />
        </SecuredRoute>
        <SecuredRoute
          allowed={[
            REACT_APP_AUTH_ROLE_ANALYST,
            REACT_APP_AUTH_ROLE_COLLECTOR,
            REACT_APP_AUTH_ROLE_OPERATOR,
          ]}
          exact
          path="/token"
        >
          <Token />
        </SecuredRoute>
        <SecuredRoute
          allowed={ALL_ROLES}
          exact
          path="/embed/:sport/:matchId/:period"
        >
          <VideoProvider>
            <Embed />
          </VideoProvider>
        </SecuredRoute>
        <SecuredRoute
          allowed={[REACT_APP_AUTH_ROLE_OPERATOR]}
          exact
          path="/trim"
        >
          <VideoProvider>
            <Trim />
          </VideoProvider>
        </SecuredRoute>
        <SecuredRoute allowed={ALL_ROLES} exact path="/toggles">
          <Toggles />
        </SecuredRoute>
        <SecuredRoute
          allowed={[REACT_APP_AUTH_ROLE_OPERATOR]}
          exact
          path="/historical-competitions"
        >
          <VideoProvider streamsOnly>
            <AlignmentVerificationCompetitionSeason />
          </VideoProvider>
        </SecuredRoute>
        <SecuredRoute
          allowed={[REACT_APP_AUTH_ROLE_OPERATOR]}
          exact
          path="/requires-offsets"
        >
          <VideoProvider streamsOnly>
            <OffsetRequired />
          </VideoProvider>
        </SecuredRoute>
        <SecuredRoute
          allowed={[REACT_APP_AUTH_ROLE_OPERATOR]}
          exact
          path="/alignment-verification"
        >
          <VideoProvider streamsOnly>
            <AlignmentVerificationStream />
          </VideoProvider>
        </SecuredRoute>
        <SecuredRoute
          allowed={[REACT_APP_AUTH_ROLE_OPERATOR]}
          exact
          path="/alignment-issues"
        >
          <VideoProvider streamsOnly>
            <AlignmentIssues />
          </VideoProvider>
        </SecuredRoute>
        <SecuredRoute allowed={ALL_ROLES} exact path="/toggles">
          <Toggles />
        </SecuredRoute>
      </PageWithNavigation>
    </Theme>
  );
}

export default App;
